import { TFolder, TItem, TResolvedItemsMap, TSparseItem, TVersionPreference } from '@modules/items';

// ---------------------------------------------------
// Enums
// ---------------------------------------------------
export enum ESubmissionStatus {
  IN_PREPARATION = 'IN_PREPARATION',
  DONE = 'DONE',
  CANCELED = 'CANCELED',
}

export enum ESubmissionItemStatus {
  BACKLOG = 'BACKLOG',
  IN_PROGRESS = 'IN_PROGRESS',
  IN_REVIEW = 'IN_REVIEW',
  ACCEPTED = 'ACCEPTED',
}

export type TSubmissionItemType = 'DOCUMENT' | 'FILE';

// ---------------------------------------------------
// Submission Item Types
// ---------------------------------------------------
/** Tracks an individual change in status for a SubmissionItem. */
export type TSubmissionItemStatusHistoryEntry = {
  status: ESubmissionItemStatus; // e.g. "IN_PROGRESS"
  date: string; // timestamp of the status change
  user: string; // user ID
};

export type TSubmissionItem = {
  item: string; // Id of the item (document or file)
  itemType: TSubmissionItemType; // 'DOCUMENT' | 'FILE'
  active: boolean; // Whether the item is active in the submission
  dueDate?: string; // Optional due date for this item
  assignee?: string; // ID of the user responsible for this item
  submittedVersionId?: string; // ID of the version submitted for this item
  status: ESubmissionItemStatus; // Current status (BACKLOG, IN_PROGRESS, DONE, ACCEPTED)
  statusHistory: TSubmissionItemStatusHistoryEntry[];
};
export type TResolvedSubmissionItem = TSubmissionItem & {
  resolvedItem: TItem;
};

// ---------------------------------------------------
// Submission Types
// ---------------------------------------------------
/** Tracks an individual change in the submission’s status. */
export type TSubmissionStatusHistoryEntry = {
  status: ESubmissionStatus; // e.g. "IN_PREPARATION"
  date: string; // timestamp of the status change
  user: string; // user ID
};

export type TSubmission = {
  _id: string; // Unique ObjectId
  active: boolean; // Whether submission is active
  archived: boolean; // Whether submission is archived
  organization: string; // ID of the organization
  product: string; // ID of the folder representing the product
  jurisdiction: string; // ID of the folder representing the jurisdiction
  title: string; // Submission title
  creator: string; // User ID of the creator
  leads: string[]; // Array of user IDs that lead this submission
  createdDate: string; // Timestamp when submission was created
  deactivatedDate?: string; // Timestamp when submission was deactivated
  archivedDate?: string; // Timestamp when submission was archived
  dueDate?: string; // Optional overall due date for the submission
  status: ESubmissionStatus; // e.g. "IN_PREPARATION"
  statusHistory: TSubmissionStatusHistoryEntry[]; // All status changes over time
  selectedItems: TSubmissionItem[]; // The array of items associated with this submission
};

/** DTO for creating a new submission. */
export type TCreateSubmissionDto = {
  title: string;
  product: string;
  jurisdiction?: string;
  dueDate?: string;
  leads: string[];
};

/** DTO for updating a submission */
export type TExportSubmissionDto = {
  itemIds?: string[];
  mode: TVersionPreference;
};

/**
 * Contains both the product’s folder data (as TFolder) and the list of submissions.
 * If your backend is returning additional fields, you can merge them here.
 */
export type TAllSubmissionsData = {
  product: TFolder;
  submissions: TSparseSubmission[];
};

/**
 * A sparse version of the TSubmission type, containing only the fields needed for the Submissions page.
 */
export type TSparseSubmission = Exclude<TSubmission, 'selectedItems'> & {
  totalSelectedItems: number;
  acceptedSelectedItems: number;
};

/**
 * Contains both the folder data and the full submission data.
 */
export type TSingleSubmissionData = {
  folder: TFolder;
  submissionData: TGetSubmissionReturn;
};

// Return type of the GET /submissions/:id endpoint
export type TGetSubmissionReturn = {
  submission: TSubmission;
  resolvedItems: TResolvedItemsMap;
};

/** Accepted item types when selecting items for a submission (includes FOLDER for folder-based selection). */
export type TSubmissionSelectableItemType = TSubmissionItemType | 'FOLDER';

export type TSelectedSubmissionItemDto = {
  itemId: string;
  itemType: TSubmissionSelectableItemType;
};

export type TSelectSubmissionItemsDto = {
  selectedItems: TSelectedSubmissionItemDto[];
};

export type TSelectItemsReturn = {
  selectedItems: TSubmissionItem[];
  resolvedItems: TResolvedItemsMap;
};

/** DTO for updating and transitioning a submission item. */
export type TUpdateSubmissionItemDto = {
  assignee?: string | null;
  dueDate?: string | null;
  submittedVersionId?: string | null;
  newStatus?: ESubmissionItemStatus;
};

/** DTO for updating submission details. */
export type TUpdateSubmissionDetailsDto = {
  title: string;
  jurisdiction: string;
  dueDate?: string;
  leads: string[];
};

/** DTO for transitioning a submission. */
export type TTransitionSubmissionDto = {
  newStatus: ESubmissionStatus;
};

// Base type used to extend `TCreateSubmissionDialogState` and `TUpdateSubmissionDialogState`
export type TBaseSubmissionDialogState = {
  isOpen: boolean;
  productName: string;
  jurisdictions: TSparseItem[];
};

/**
 * TCreateSubmissionDialogState
 * ---------------------------------
 * Single state object for the Create Submission dialog.
 * It encapsulates all necessary properties.
 */
export type TCreateSubmissionDialogState = TBaseSubmissionDialogState & {
  productId: string;
};

/**
 * TUpdateSubmissionDialogState
 * ---------------------------------
 * Single state object for the Update Submission dialog.
 * Contains the submission to update along with additional context.
 */
export type TUpdateSubmissionDialogState = TBaseSubmissionDialogState & {
  submission: TSubmission;
};

/**
 * TSubmissionItemInfo
 * ---------------------------------
 * This type is used by the document / file view.
 */
export type TSubmissionItemInfo = Omit<TSubmission, 'selectedItems'> & {
  submissionItem: TSubmissionItem;
};
